import { useEffect, useState } from 'react';
import { X, Save, Star, Sparkles, Award } from 'lucide-react';
import { topPicksPoolApi, newReleasesApi, bookOfTheWeekApi } from '../../services/adminApi';
import AgeRangeField from './AgeRangeField';
import { formatAgeRangeDraft, parseAgeRange, validateAgeRangeDraft } from '../../utils/ageRange';
import { getSuggestedTopPicksAgeScope } from '../../utils/topPicksAgeScope';

const LEGACY_GENRE_OPTIONS = [
  'animals',
  'adventure',
  'mystery',
  'poetry',
  'classics',
  'fantasy',
  'sci-fi',
  'funny',
  'history',
  'non-fiction'
];

const BookQuickActionModal = ({ isOpen, onClose, book, action, onSuccess }) => {
  const [formData, setFormData] = useState({
    age_group: '3-5',
    primary_genre: '',
    secondary_genres: [],
    note: '',
    rank: 1,
    month: new Date().toISOString().slice(0, 7) // YYYY-MM format
  });
  const [ageRangeDraft, setAgeRangeDraft] = useState(() => parseAgeRange('3-5'));
  const [signalPreview, setSignalPreview] = useState(null);
  const [previewBook, setPreviewBook] = useState(null);
  const [signalPreviewLoading, setSignalPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ageScopeTouched, setAgeScopeTouched] = useState(false);
  const suggestedAgeScope = getSuggestedTopPicksAgeScope(previewBook?.reading_age);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setFormData({
      age_group: '3-5',
      primary_genre: '',
      secondary_genres: [],
      note: '',
      rank: 1,
      month: new Date().toISOString().slice(0, 7)
    });
    setAgeRangeDraft(parseAgeRange('3-5'));
    setSignalPreview(null);
    setPreviewBook(null);
    setError(null);
    setAgeScopeTouched(false);
  }, [isOpen, action, book]);

  useEffect(() => {
    if (!isOpen || action !== 'top-picks' || !book?.dbId) {
      return;
    }

    let cancelled = false;

    const loadSignalPreview = async () => {
      try {
        setSignalPreviewLoading(true);
        const result = await topPicksPoolApi.previewTopPicksSignals(book.dbId);
        if (!cancelled) {
          setSignalPreview(result.signals || null);
          setPreviewBook(result.book || null);

          const nextSuggestedAgeScope = getSuggestedTopPicksAgeScope(result.book?.reading_age);
          if (!ageScopeTouched && nextSuggestedAgeScope) {
            setAgeRangeDraft(nextSuggestedAgeScope.draft);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setSignalPreview({
            available: false,
            requires_manual_fallback: true,
            preview_error: err.message || 'Failed to preview Top Picks signals'
          });
          setPreviewBook(null);
        }
      } finally {
        if (!cancelled) {
          setSignalPreviewLoading(false);
        }
      }
    };

    loadSignalPreview();

    return () => {
      cancelled = true;
    };
  }, [action, ageScopeTouched, book, isOpen]);

  const handleSecondaryGenreChange = (genre) => {
    setFormData((prev) => ({
      ...prev,
      secondary_genres: prev.secondary_genres.includes(genre)
        ? prev.secondary_genres.filter((entry) => entry !== genre)
        : [...prev.secondary_genres, genre]
    }));
  };

  const handleAgeRangeChange = (draft) => {
    setAgeScopeTouched(true);
    setAgeRangeDraft(draft);
  };

  const getActionConfig = () => {
    switch (action) {
      case 'top-picks':
        return {
          title: 'Add to Top Picks Pool',
          icon: Star,
          color: 'blue',
          submitText: 'Add to Top Picks'
        };
      case 'new-releases':
        return {
          title: 'Add to New Releases',
          icon: Sparkles,
          color: 'green',
          submitText: 'Add to New Releases'
        };
      case 'book-of-week':
        return {
          title: 'Set as Book of the Week',
          icon: Award,
          color: 'yellow',
          submitText: 'Create Draft'
        };
      default:
        return {
          title: 'Quick Action',
          icon: X,
          color: 'gray',
          submitText: 'Submit'
        };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!book) return;

    const requiresAgeRange = action !== 'new-releases';

    if (requiresAgeRange) {
      const ageGroupError = validateAgeRangeDraft(ageRangeDraft);
      if (ageGroupError) {
        setError(ageGroupError);
        return;
      }
    }

    const requiresManualFallback = action === 'top-picks' && signalPreview?.requires_manual_fallback;
    if (requiresManualFallback && !formData.primary_genre) {
      setError('Canonical book signals are unavailable for this book. Select a legacy primary genre fallback.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const bookId = book.dbId;
      const ageGroup = formatAgeRangeDraft(ageRangeDraft);

      if (!bookId) {
        throw new Error('This book is not stored in the Bukito database yet.');
      }

      const data = {
        book_id: bookId,
        note: formData.note.trim() || null
      };

      if (requiresAgeRange) {
        data.age_group = ageGroup;
      }

      switch (action) {
        case 'top-picks':
          await topPicksPoolApi.createTopPicksPoolRecord(
            requiresManualFallback
              ? {
                  ...data,
                  primary_genre: formData.primary_genre,
                  secondary_genres: formData.secondary_genres.length > 0 ? formData.secondary_genres : null,
                  rank: parseInt(formData.rank, 10),
                  status: 'active'
                }
              : {
                  ...data,
                  rank: parseInt(formData.rank, 10),
                  status: 'active'
                }
          );
          break;
        case 'new-releases':
          await newReleasesApi.addNewReleasesItem({
            ...data,
            month: formData.month,
            rank: parseInt(formData.rank, 10)
          });
          break;
        case 'book-of-week':
          await bookOfTheWeekApi.createBookOfTheWeekRecord(data);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      onSuccess?.(action, book);
      onClose();
    } catch (err) {
      console.error(`Failed to ${action}:`, err);
      let errorMessage = `Failed to ${action.replace('-', ' ')}`;

      if (err.message) {
        if (err.message.includes('not stored in the Bukito database')) {
          errorMessage = 'This book cannot be added yet because it is not stored in the Bukito database.';
        } else
        if (err.message.includes('Book not found')) {
          errorMessage = 'Book not found. The book may need to be added to the database first.';
        } else if (err.message.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (err.message.includes('401') || err.message.includes('403')) {
          errorMessage = 'Permission denied. Please check your admin access.';
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !book) return null;

  const config = getActionConfig();
  const IconComponent = config.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <IconComponent className={`w-5 h-5 text-${config.color}-600`} />
            <h2 className="text-lg font-semibold text-text-primary">{config.title}</h2>
          </div>
          <button onClick={onClose} disabled={loading} className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          {/* Book Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-start space-x-3">
              <div className="w-12 h-16 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                {book.coverUrl ? (
                  <img src={book.coverUrl} alt={book.title} className="w-full h-full object-cover rounded" />
                ) : (
                  <span className="text-gray-500 text-xs">No Cover</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-text-primary text-sm truncate">{book.title}</h3>
                <p className="text-text-secondary text-xs">
                  by {Array.isArray(book.authors) ? book.authors.join(', ') : book.authors || 'Unknown Author'}
                </p>
                <p className="text-xs text-text-secondary">ID: {book.dbId || book.id}</p>
                {book.dbId && String(book.dbId) !== String(book.id) && (
                  <p className="text-xs text-text-secondary">Source ID: {book.id}</p>
                )}
              </div>
            </div>
          </div>

          {action !== 'new-releases' && (
            <AgeRangeField
              label={action === 'top-picks' ? 'Placement Age Scope' : 'Age Range'}
              draft={ageRangeDraft}
              onChange={handleAgeRangeChange}
              error={null}
              required
              disabled={loading}
              helperText={action === 'top-picks'
                ? (
                    suggestedAgeScope
                      ? `Prefilled from canonical reading age (${previewBook.reading_age}) when available. Top Picks still uses this temporary scope for runtime compatibility.`
                      : 'Top Picks still uses a temporary placement age scope for runtime compatibility.'
                  )
                : undefined}
            />
          )}

          {/* Action-specific fields */}
          {action === 'top-picks' && (
            <>
              <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                {signalPreviewLoading && 'Checking canonical book metadata for Top Picks signals...'}
                {!signalPreviewLoading && signalPreview && !signalPreview.requires_manual_fallback && (
                  <>
                    <p>Top Picks signals will be system-derived for this book.</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full bg-white px-2 py-1 text-blue-900">
                        Primary: {signalPreview.primary_genre}
                      </span>
                      {signalPreview.secondary_genres?.map((genre) => (
                        <span key={genre} className="rounded-full bg-blue-100 px-2 py-1 text-blue-900">
                          {genre}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                {!signalPreviewLoading && signalPreview?.requires_manual_fallback && (
                  <p>
                    Canonical book metadata is not strong enough for this book yet. Use the legacy genre fallback below.
                  </p>
                )}
              </div>

              {signalPreview?.requires_manual_fallback && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Legacy Primary Genre *</label>
                    <select
                      value={formData.primary_genre}
                      onChange={(e) => setFormData(prev => ({ ...prev, primary_genre: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
                      required
                      disabled={loading}
                    >
                      <option value="">Select a fallback genre</option>
                      {LEGACY_GENRE_OPTIONS.map((genre) => (
                        <option key={genre} value={genre}>{genre}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Legacy Secondary Genres</label>
                    <div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 bg-white p-3">
                      {LEGACY_GENRE_OPTIONS.map((genre) => (
                        <label key={genre} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formData.secondary_genres.includes(genre)}
                            onChange={() => handleSecondaryGenreChange(genre)}
                            disabled={loading}
                          />
                          <span>{genre}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Rank *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.rank}
                  onChange={(e) => setFormData(prev => ({ ...prev, rank: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
                  required
                  disabled={loading}
                />
              </div>
            </>
          )}

          {action === 'new-releases' && (
            <>
              <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
                New Releases now uses the book&apos;s canonical reading age. Select the month and placement only.
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Month *</label>
                <input
                  type="month"
                  value={formData.month}
                  onChange={(e) => setFormData(prev => ({ ...prev, month: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Rank *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.rank}
                  onChange={(e) => setFormData(prev => ({ ...prev, rank: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
                  required
                  disabled={loading}
                />
              </div>
            </>
          )}

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Note</label>
            <textarea
              value={formData.note}
              onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
              placeholder="Optional note about this selection..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200 h-20"
              disabled={loading}
            />
          </div>

          <div className="sticky bottom-0 -mx-6 flex justify-end space-x-3 border-t border-gray-200 bg-white px-6 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-200 text-text-secondary rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`btn-primary flex items-center space-x-2 disabled:opacity-50`}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{loading ? 'Adding...' : config.submitText}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookQuickActionModal;
